import { useCallback, useEffect, useState } from "react";
import { UserPlus, KeyRound, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";

const inputCls = "bg-[#0B0E14] border border-[#253044] rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:border-cyan-500";

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "editor" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/users", form);
      toast.success(`${form.name} added as ${form.role}`);
      setForm({ name: "", email: "", password: "", role: "editor" });
      load();
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  const patch = async (u, body, msg) => {
    try { await api.patch(`/auth/users/${u.id}`, body); toast.success(msg); load(); } catch (err) { toast.error(errorText(err)); }
  };
  const resetPassword = (u) => {
    const p = window.prompt(`New password for ${u.email} (min 8 chars)`);
    if (p) patch(u, { password: p }, "Password updated");
  };

  return (
    <div data-testid="team-view" className="space-y-4 fade-up">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Team</h1>
        <p className="text-sm text-slate-400 mt-1">One account per person. <b>editor</b> = upload episodes, choose Fast / HQ / Both, run, monitor, download stems. <b>admin</b> = also engines, GPU / RunPod settings, costs, team. QC decisions and notes are attributed to the signed-in reviewer.</p>
      </div>
      <form onSubmit={create} className="panel p-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1"><span className="micro">Name</span><input data-testid="team-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} w-44`} /></label>
        <label className="flex flex-col gap-1"><span className="micro">Email</span><input data-testid="team-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} w-56 font-mono`} /></label>
        <label className="flex flex-col gap-1"><span className="micro">Password</span><input data-testid="team-password-input" type="text" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={`${inputCls} w-44 font-mono`} /></label>
        <label className="flex flex-col gap-1"><span className="micro">Role</span>
          <select data-testid="team-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={`${inputCls} w-32`}>
            <option value="editor">editor</option><option value="admin">admin</option>
          </select></label>
        <button data-testid="team-create-button" disabled={busy} className="h-9 px-3 rounded-sm bg-[#E11D48] hover:bg-[#be123c] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"><UserPlus size={14} /> Add account</button>
      </form>
      <div className="panel overflow-x-auto">
        <table data-testid="team-table" className="w-full text-sm">
          <thead><tr className="text-left micro border-b border-[#253044]"><th className="px-3 py-2.5">Name</th><th className="px-3 py-2.5">Email</th><th className="px-3 py-2.5">Role</th><th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5">Created</th><th className="px-3 py-2.5 text-right">Actions</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`team-row-${u.id}`} className="row-hover border-b border-[#1c2433] last:border-0">
                <td className="px-3 py-2.5 font-medium">{u.name}{u.id === user.id ? <span className="ml-2 text-[10px] text-slate-500">(you)</span> : null}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2.5">
                  <select data-testid={`team-role-${u.id}`} value={u.role} disabled={u.id === user.id} onChange={(e) => patch(u, { role: e.target.value }, "Role updated")} className={`${inputCls} py-1 text-xs`}>
                    <option value="editor">editor</option><option value="admin">admin</option>
                  </select>
                </td>
                <td className="px-3 py-2.5"><span data-testid={`team-status-${u.id}`} className={`font-mono text-[11px] uppercase ${u.active ? "text-emerald-400" : "text-slate-500"}`}>{u.active ? "active" : "disabled"}</span></td>
                <td className="px-3 py-2.5 text-xs text-slate-400">{fmtDate(u.created_at)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`team-reset-${u.id}`} onClick={() => resetPassword(u)} className="flex items-center gap-1 px-2.5 py-1 rounded-sm border border-[#253044] hover:bg-[#1E2738] text-xs"><KeyRound size={12} /> Password</button>
                    {u.id !== user.id && (
                      <button data-testid={`team-toggle-${u.id}`} onClick={() => patch(u, { active: !u.active }, u.active ? "Account disabled" : "Account enabled")} className="flex items-center gap-1 px-2.5 py-1 rounded-sm border border-[#253044] hover:bg-[#1E2738] text-xs">
                        {u.active ? <><UserX size={12} /> Disable</> : <><UserCheck size={12} /> Enable</>}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
